/**
 * BookingResourceConfigDetailContent — Creative settings page for Booking Resource Config.
 *
 * Inspired by the PricingRuleCreatePage wizard pattern:
 *   - Pill selectors for slot duration
 *   - Visual weekly calendar mini-bar
 *   - Preset quick-fill buttons
 *   - Live preview showing what customers see
 *   - Clean section labels + card pattern
 *   - Stepper controls for numbers
 *
 * All changes flow through onFieldChange to the parent's save mechanism.
 */

import { useCallback, useMemo } from "react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/i18n/useLanguage";
import {
  Clock, Calendar, Users, Layers, CalendarRange, Eye, Copy,
  Sun, Moon, Check, Plus, Minus, Timer,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { CARD } from "@/config/design-tokens";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Props {
  formData: Record<string, any>;
  onFieldChange: (fieldname: string, value: any) => void;
}

type HourRow = {
  day_of_week: string;
  start_time: string;
  end_time: string;
  is_closed: 0 | 1;
  name?: string;
  idx?: number;
};


// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const DAY_SHORT: Record<string, string> = {
  Monday: "M", Tuesday: "T", Wednesday: "W", Thursday: "T",
  Friday: "F", Saturday: "S", Sunday: "S",
};
const DAY_TH: Record<string, string> = {
  Monday: "\u0e08\u0e31\u0e19\u0e17\u0e23\u0e4c", Tuesday: "\u0e2d\u0e31\u0e07\u0e04\u0e32\u0e23", Wednesday: "\u0e1e\u0e38\u0e18", Thursday: "\u0e1e\u0e24\u0e2b\u0e31\u0e2a\u0e1a\u0e14\u0e35",
  Friday: "\u0e28\u0e38\u0e01\u0e23\u0e4c", Saturday: "\u0e40\u0e2a\u0e32\u0e23\u0e4c", Sunday: "\u0e2d\u0e32\u0e17\u0e34\u0e15\u0e22\u0e4c",
};
const DAY_SHORT_TH: Record<string, string> = {
  Monday: "\u0e08.", Tuesday: "\u0e2d.", Wednesday: "\u0e1e.", Thursday: "\u0e1e\u0e24.",
  Friday: "\u0e28.", Saturday: "\u0e2a.", Sunday: "\u0e2d\u0e32.",
};

const DEFAULT_START = "09:00";
const DEFAULT_END = "18:00";

const SLOT_PRESETS = [
  { value: 30, label: "30m" },
  { value: 45, label: "45m" },
  { value: 60, label: "1hr" },
  { value: 90, label: "1.5hr" },
  { value: 120, label: "2hr" },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Normalize Frappe time (e.g. "9:00:00", "09:00", "18:00:00") to "HH:MM" for <input type="time"> */
function normalizeTime(t: string | null | undefined): string {
  if (!t) return "";
  const parts = String(t).split(":");
  if (parts.length < 2) return "";
  const h = parts[0].padStart(2, "0");
  const m = parts[1].padStart(2, "0");
  return `${h}:${m}`;
}

function formatTime12(t: string): string {
  if (!t) return "";
  const norm = normalizeTime(t);
  if (!norm) return "";
  const [h, m] = norm.split(":");
  const hour = parseInt(h, 10);
  const ampm = hour >= 12 ? "PM" : "AM";
  const h12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${h12}:${m} ${ampm}`;
}

function formatDuration(mins: number, th: boolean): string {
  if (!mins) return "\u2014";
  if (mins >= 60 && mins % 60 === 0) return `${mins / 60} ${th ? "\u0e0a\u0e21." : "hr"}`;
  if (mins >= 60) return `${Math.floor(mins / 60)}h ${mins % 60}m`;
  return `${mins} ${th ? "\u0e19\u0e32\u0e17\u0e35" : "min"}`;
}

// ---------------------------------------------------------------------------
// Section Label (matches PricingRuleCreatePage)
// ---------------------------------------------------------------------------

function SectionLabel({ children }: { children: React.ReactNode }) {
  return <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{children}</h2>;
}

// ---------------------------------------------------------------------------
// Stepper — +/- control for numbers
// ---------------------------------------------------------------------------

function Stepper({ value, onChange, min = 1, max = 99, label, suffix }: {
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  label: string;
  suffix?: string;
}) {
  return (
    <div className="flex items-center justify-between px-4 py-4">
      <span className="text-sm font-medium">{label}</span>
      <div className="flex items-center gap-3">
        <Button
          variant="outline"
          size="icon"
          className="h-8 w-8 rounded"
          onClick={() => onChange(Math.max(min, value - 1))}
          disabled={value <= min}
        >
          <Minus className="w-3 h-3" />
        </Button>
        <span className="text-lg font-semibold tabular-nums w-8 text-center text-primary">
          {value}
        </span>
        <Button
          variant="outline"
          size="icon"
          className="h-8 w-8 rounded"
          onClick={() => onChange(Math.min(max, value + 1))}
          disabled={value >= max}
        >
          <Plus className="w-3 h-3" />
        </Button>
        {suffix && <span className="text-xs text-muted-foreground ml-0.5">{suffix}</span>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Weekly Mini-Bar — visual at-a-glance calendar
// ---------------------------------------------------------------------------

function WeeklyMiniBar({ hours, th }: { hours: HourRow[]; th: boolean }) {
  return (
    <div className="flex gap-1">
      {DAYS.map((day) => {
        const row = hours.find(h => h.day_of_week === day);
        const isOpen = row && !row.is_closed;
        const isWeekend = day === "Saturday" || day === "Sunday";
        return (
          <div
            key={day}
            className={cn(
              "flex-1 flex flex-col items-center gap-1 py-2 rounded-md transition-colors",
              isOpen
                ? isWeekend ? "bg-blue-50 dark:bg-blue-950/30" : "bg-primary/[0.06]"
                : "bg-muted/40",
            )}
          >
            <span className={cn(
              "text-[10px] font-semibold uppercase",
              isOpen
                ? isWeekend ? "text-blue-600 dark:text-blue-400" : "text-primary"
                : "text-muted-foreground/50",
            )}>
              {th ? DAY_SHORT_TH[day] : DAY_SHORT[day]}
            </span>
            <div className={cn(
              "w-1.5 h-1.5 rounded-full",
              isOpen ? "bg-primary" : "bg-muted-foreground/20",
            )} />
            {isOpen && row && (
              <span className="text-[9px] tabular-nums text-muted-foreground">
                {normalizeTime(row.start_time) || DEFAULT_START}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function BookingResourceConfigDetailContent({ formData, onFieldChange }: Props) {
  const { language } = useLanguage();
  const th = language === "th";

  // --- Parsed data ---
  const status = String(formData.status ?? "Draft");
  const isActive = status === "Active";
  const slotMins = Number(formData.slot_duration_minutes) || 60;
  const maxConcurrent = Number(formData.max_concurrent_bookings) || 1;
  const minSlots = Number(formData.min_booking_slots) || 1;
  const maxSlots = Number(formData.max_booking_slots) || 4;
  const advanceDays = Number(formData.advance_booking_days) || 30;

  const operatingHours = useMemo(() => {
    const rows = (formData.operating_hours ?? []) as HourRow[];
    const byDay = new Map(rows.map(r => [r.day_of_week, r]));
    return DAYS.map((day, i) => byDay.get(day) ?? {
      day_of_week: day, start_time: DEFAULT_START, end_time: DEFAULT_END, is_closed: 1 as const, idx: i + 1,
    });
  }, [formData.operating_hours]);

  // --- Handlers ---
  const updateHours = useCallback((newHours: HourRow[]) => {
    onFieldChange("operating_hours", newHours.map((h, i) => ({ ...h, idx: i + 1 })));
  }, [onFieldChange]);

  const toggleDay = useCallback((day: string) => {
    const updated = operatingHours.map(h =>
      h.day_of_week === day
        ? { ...h, is_closed: h.is_closed ? 0 as const : 1 as const, start_time: h.start_time || DEFAULT_START, end_time: h.end_time || DEFAULT_END }
        : h,
    );
    updateHours(updated);
  }, [operatingHours, updateHours]);

  const updateDayTime = useCallback((day: string, field: "start_time" | "end_time", value: string) => {
    const updated = operatingHours.map(h =>
      h.day_of_week === day ? { ...h, [field]: value } : h,
    );
    updateHours(updated);
  }, [operatingHours, updateHours]);

  const setWeekdayHours = useCallback((start: string, end: string) => {
    const weekdays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
    const updated = operatingHours.map(h =>
      weekdays.includes(h.day_of_week)
        ? { ...h, is_closed: 0 as const, start_time: start, end_time: end }
        : h,
    );
    updateHours(updated);
  }, [operatingHours, updateHours]);

  const copyToAll = useCallback((sourceDay: string) => {
    const source = operatingHours.find(h => h.day_of_week === sourceDay);
    if (!source) return;
    const updated = operatingHours.map(h => ({
      ...h, start_time: source.start_time, end_time: source.end_time, is_closed: source.is_closed,
    }));
    updateHours(updated);
  }, [operatingHours, updateHours]);

  // --- Live preview: generate sample slots for today ---
  const previewSlots = useMemo(() => {
    const today = new Date();
    const dayName = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"][today.getDay()];
    const todayHours = operatingHours.find(h => h.day_of_week === dayName);
    if (!todayHours || todayHours.is_closed) return { day: dayName, closed: true, slots: [] };

    const [sh, sm] = (normalizeTime(todayHours.start_time) || "09:00").split(":").map(Number);
    const [eh, em] = (normalizeTime(todayHours.end_time) || "18:00").split(":").map(Number);
    const startMin = sh * 60 + sm;
    const endMin = eh * 60 + em;
    const dur = slotMins || 60;
    const slots: string[] = [];
    for (let m = startMin; m + dur <= endMin && slots.length < 16; m += dur) {
      const hh = Math.floor(m / 60).toString().padStart(2, "0");
      const mm = (m % 60).toString().padStart(2, "0");
      slots.push(`${hh}:${mm}`);
    }
    return { day: dayName, closed: false, slots };
  }, [operatingHours, slotMins]);

  const openDayCount = operatingHours.filter(h => !h.is_closed).length;

  return (
    <div className="grid grid-cols-[1fr_320px] gap-10 pb-10">
      {/* ── Left: Configuration ── */}
      <div className="space-y-8">

        {/* Status banner */}
        <div className={cn(
          "flex items-center justify-between rounded-lg px-5 py-4 transition-colors",
          isActive ? "bg-primary/[0.06]" : "bg-muted/30",
        )}>
          <div className="flex items-center gap-3">
            <div className={cn(
              "w-10 h-10 rounded-lg flex items-center justify-center",
              isActive ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground",
            )}>
              <Calendar className="w-5 h-5" />
            </div>
            <div>
              <p className={cn("text-sm font-medium", isActive ? "text-primary" : "text-foreground")}>
                {isActive
                  ? (th ? "\u0e40\u0e1b\u0e34\u0e14\u0e43\u0e0a\u0e49\u0e07\u0e32\u0e19 \u2014 \u0e25\u0e39\u0e01\u0e04\u0e49\u0e32\u0e2a\u0e32\u0e21\u0e32\u0e23\u0e16\u0e08\u0e2d\u0e07\u0e44\u0e14\u0e49" : "Active \u2014 customers can book")
                  : (th ? "\u0e09\u0e1a\u0e31\u0e1a\u0e23\u0e48\u0e32\u0e07 \u2014 \u0e22\u0e31\u0e07\u0e44\u0e21\u0e48\u0e40\u0e1b\u0e34\u0e14\u0e43\u0e2b\u0e49\u0e08\u0e2d\u0e07" : "Draft \u2014 not yet accepting bookings")
                }
              </p>
            </div>
          </div>
          <Switch
            checked={isActive}
            onCheckedChange={(checked) => onFieldChange("status", checked ? "Active" : "Draft")}
          />
        </div>

        {/* Slot Duration — pill selector */}
        <div className="space-y-3">
          <SectionLabel>{th ? "\u0e23\u0e30\u0e22\u0e30\u0e40\u0e27\u0e25\u0e32\u0e2a\u0e25\u0e47\u0e2d\u0e15" : "Slot Duration"}</SectionLabel>
          <div className="flex gap-2">
            {SLOT_PRESETS.map((preset) => {
              const active = slotMins === preset.value;
              return (
                <button
                  key={preset.value}
                  type="button"
                  onClick={() => onFieldChange("slot_duration_minutes", preset.value)}
                  className={cn(
                    "relative px-5 py-3 rounded-lg text-sm font-medium transition-all",
                    active
                      ? "bg-primary text-primary-foreground shadow-sm"
                      : "bg-white dark:bg-card border border-border hover:border-muted-foreground/30 hover:shadow-sm text-foreground",
                  )}
                >
                  {preset.label}
                  {active && (
                    <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-primary-foreground flex items-center justify-center">
                      <Check className="w-2.5 h-2.5 text-primary" />
                    </div>
                  )}
                </button>
              );
            })}
            {/* Custom option if current value doesn't match presets */}
            {!SLOT_PRESETS.some(p => p.value === slotMins) && (
              <div className="px-5 py-3 rounded-lg text-sm font-medium bg-primary text-primary-foreground shadow-sm">
                {slotMins}m
              </div>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            {th
              ? `\u0e41\u0e15\u0e48\u0e25\u0e30\u0e2a\u0e25\u0e47\u0e2d\u0e15\u0e08\u0e30\u0e21\u0e35\u0e23\u0e30\u0e22\u0e30\u0e40\u0e27\u0e25\u0e32 ${formatDuration(slotMins, th)} \u0e25\u0e39\u0e01\u0e04\u0e49\u0e32\u0e08\u0e2d\u0e07\u0e44\u0e14\u0e49 ${minSlots}-${maxSlots} \u0e2a\u0e25\u0e47\u0e2d\u0e15\u0e15\u0e48\u0e2d\u0e04\u0e23\u0e31\u0e49\u0e07`
              : `Each slot is ${formatDuration(slotMins, th)}. Customers can book ${minSlots}\u2013${maxSlots} consecutive slots.`
            }
          </p>
        </div>

        {/* Booking Limits */}
        <div className="space-y-3">
          <SectionLabel>{th ? "\u0e01\u0e32\u0e23\u0e08\u0e2d\u0e07" : "Booking Limits"}</SectionLabel>
          <div className={cn("rounded-xl border border-border bg-white dark:bg-card", CARD)}>
            <Stepper
              label={th ? "\u0e08\u0e2d\u0e07\u0e1e\u0e23\u0e49\u0e2d\u0e21\u0e01\u0e31\u0e19\u0e2a\u0e39\u0e07\u0e2a\u0e38\u0e14" : "Max concurrent bookings"}
              value={maxConcurrent}
              onChange={(v) => onFieldChange("max_concurrent_bookings", v)}
              min={1}
              max={50}
              suffix={th ? "\u0e04\u0e19" : ""}
            />
            <div className="border-t border-border/50 mx-4" />
            <Stepper
              label={th ? "\u0e2a\u0e25\u0e47\u0e2d\u0e15\u0e15\u0e48\u0e33\u0e2a\u0e38\u0e14\u0e15\u0e48\u0e2d\u0e01\u0e32\u0e23\u0e08\u0e2d\u0e07" : "Min slots per booking"}
              value={minSlots}
              onChange={(v) => onFieldChange("min_booking_slots", v)}
              min={1}
              max={maxSlots}
            />
            <div className="border-t border-border/50 mx-4" />
            <Stepper
              label={th ? "\u0e2a\u0e25\u0e47\u0e2d\u0e15\u0e2a\u0e39\u0e07\u0e2a\u0e38\u0e14\u0e15\u0e48\u0e2d\u0e01\u0e32\u0e23\u0e08\u0e2d\u0e07" : "Max slots per booking"}
              value={maxSlots}
              onChange={(v) => onFieldChange("max_booking_slots", v)}
              min={minSlots}
              max={24}
            />
            <div className="border-t border-border/50 mx-4" />
            <Stepper
              label={th ? "\u0e08\u0e2d\u0e07\u0e25\u0e48\u0e27\u0e07\u0e2b\u0e19\u0e49\u0e32\u0e44\u0e14\u0e49\u0e2a\u0e39\u0e07\u0e2a\u0e38\u0e14" : "Advance booking limit"}
              value={advanceDays}
              onChange={(v) => onFieldChange("advance_booking_days", v)}
              min={1}
              max={365}
              suffix={th ? "\u0e27\u0e31\u0e19" : "days"}
            />
          </div>
        </div>

        {/* Weekly Schedule */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <SectionLabel>{th ? "\u0e15\u0e32\u0e23\u0e32\u0e07\u0e40\u0e27\u0e25\u0e32" : "Weekly Schedule"}</SectionLabel>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => setWeekdayHours("09:00", "18:00")}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded text-[11px] font-medium text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors"
              >
                <Sun className="w-3 h-3" />
                {th ? "\u0e08.-\u0e28. 9-18" : "Weekdays 9-6"}
              </button>
              <button
                type="button"
                onClick={() => setWeekdayHours("10:00", "22:00")}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded text-[11px] font-medium text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors"
              >
                <Moon className="w-3 h-3" />
                {th ? "\u0e08.-\u0e28. 10-22" : "Weekdays 10-10"}
              </button>
            </div>
          </div>

          {/* Mini calendar bar */}
          <WeeklyMiniBar hours={operatingHours} th={th} />

          {/* Day rows */}
          <div className={cn("rounded-xl border border-border bg-white dark:bg-card overflow-hidden", CARD)}>
            {operatingHours.map((row, i) => {
              const isOpen = !row.is_closed;
              const dayLabel = th ? DAY_TH[row.day_of_week] : row.day_of_week;
              const isWeekend = row.day_of_week === "Saturday" || row.day_of_week === "Sunday";
              return (
                <div key={row.day_of_week}>
                  {i > 0 && <div className="border-t border-border/50 mx-4" />}
                  <div className={cn(
                    "flex items-center gap-4 px-4 py-3.5 transition-colors",
                    !isOpen && "bg-muted/20",
                  )}>
                    <Switch
                      checked={isOpen}
                      onCheckedChange={() => toggleDay(row.day_of_week)}
                    />
                    <span className={cn(
                      "text-sm font-medium w-[100px]",
                      isOpen ? "text-foreground" : "text-muted-foreground/50",
                      isWeekend && isOpen && "text-blue-600 dark:text-blue-400",
                    )}>
                      {dayLabel}
                    </span>

                    {isOpen ? (
                      <div className="flex items-center gap-2 flex-1">
                        <Input
                          type="time"
                          value={normalizeTime(row.start_time) || DEFAULT_START}
                          onChange={(e) => updateDayTime(row.day_of_week, "start_time", e.target.value)}
                          className="h-8 w-[120px] text-[13px] font-mono tabular-nums"
                        />
                        <span className="text-muted-foreground/40">{"\u2192"}</span>
                        <Input
                          type="time"
                          value={normalizeTime(row.end_time) || DEFAULT_END}
                          onChange={(e) => updateDayTime(row.day_of_week, "end_time", e.target.value)}
                          className="h-8 w-[120px] text-[13px] font-mono tabular-nums"
                        />
                        <button
                          type="button"
                          onClick={() => copyToAll(row.day_of_week)}
                          className="ml-auto p-1.5 rounded text-muted-foreground/30 hover:text-muted-foreground hover:bg-muted/40 transition-colors"
                          title={th ? "\u0e04\u0e31\u0e14\u0e25\u0e2d\u0e01\u0e44\u0e1b\u0e17\u0e38\u0e01\u0e27\u0e31\u0e19" : "Copy to all days"}
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground/40 italic">
                        {th ? "\u0e1b\u0e34\u0e14\u0e17\u0e33\u0e01\u0e32\u0e23" : "Closed"}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Pricing — from Item Price List (ERPNext source of truth) */}
        <div className="space-y-3">
          <SectionLabel>{th ? "\u0e23\u0e32\u0e04\u0e32" : "Pricing"}</SectionLabel>
          <div className={cn("rounded-xl border border-border bg-white dark:bg-card", CARD)}>
            <div className="px-5 py-5">
              <p className="text-sm text-muted-foreground mb-1">
                {th ? "\u0e23\u0e32\u0e04\u0e32\u0e15\u0e48\u0e2d\u0e2a\u0e25\u0e47\u0e2d\u0e15\u0e21\u0e32\u0e08\u0e32\u0e01\u0e23\u0e32\u0e04\u0e32\u0e2a\u0e34\u0e19\u0e04\u0e49\u0e32 (Price List)" : "Slot price comes from the Item\u2019s selling price (Price List)"}
              </p>
              <p className="text-xs text-muted-foreground/60">
                {th
                  ? "\u0e41\u0e01\u0e49\u0e44\u0e02\u0e23\u0e32\u0e04\u0e32\u0e44\u0e14\u0e49\u0e17\u0e35\u0e48\u0e2b\u0e19\u0e49\u0e32\u0e2a\u0e34\u0e19\u0e04\u0e49\u0e32 \u0e2b\u0e23\u0e37\u0e2d\u0e43\u0e0a\u0e49 Pricing Rules \u0e2a\u0e33\u0e2b\u0e23\u0e31\u0e1a\u0e23\u0e32\u0e04\u0e32\u0e1e\u0e34\u0e40\u0e28\u0e29\u0e15\u0e32\u0e21\u0e27\u0e31\u0e19/\u0e0a\u0e48\u0e27\u0e07\u0e40\u0e27\u0e25\u0e32"
                  : "Edit the price on the Item page, or use Pricing Rules for day/time-based overrides"
                }
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Right: Live Preview ── */}
      <div className="space-y-6">
        {/* Sticky preview */}
        <div className="sticky top-6 space-y-5">

          {/* Customer booking widget mockup */}
          <div className="rounded-xl border border-border bg-white dark:bg-card overflow-hidden shadow-sm">
            {/* Widget header */}
            <div className="bg-gradient-to-r from-primary/10 to-primary/5 px-5 py-4">
              <div className="flex items-center gap-2 mb-1">
                <Eye className="w-4 h-4 text-primary" />
                <span className="text-xs font-semibold text-primary uppercase tracking-wide">
                  {th ? "\u0e15\u0e31\u0e27\u0e2d\u0e22\u0e48\u0e32\u0e07\u0e2b\u0e19\u0e49\u0e32\u0e23\u0e49\u0e32\u0e19" : "Customer view"}
                </span>
              </div>
              <p className="text-[11px] text-muted-foreground">
                {th ? "\u0e25\u0e39\u0e01\u0e04\u0e49\u0e32\u0e08\u0e30\u0e40\u0e2b\u0e47\u0e19\u0e41\u0e1a\u0e1a\u0e19\u0e35\u0e49\u0e40\u0e21\u0e37\u0e48\u0e2d\u0e08\u0e2d\u0e07" : "This is what customers see when booking"}
              </p>
            </div>

            {/* Mock date picker */}
            <div className="px-5 py-4 border-b border-border/50">
              <p className="text-xs text-muted-foreground mb-2">
                {th ? "\u0e40\u0e25\u0e37\u0e2d\u0e01\u0e27\u0e31\u0e19\u0e17\u0e35\u0e48" : "Select date"}
              </p>
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-muted/30 border border-border/50">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-medium">
                  {th ? DAY_TH[previewSlots.day] : previewSlots.day}
                  <span className="text-muted-foreground font-normal"> \u2014 {th ? "\u0e27\u0e31\u0e19\u0e19\u0e35\u0e49" : "Today"}</span>
                </span>
              </div>
            </div>

            {/* Available slots */}
            <div className="px-5 py-4">
              <p className="text-xs text-muted-foreground mb-3">
                {th ? "\u0e40\u0e27\u0e25\u0e32\u0e27\u0e48\u0e32\u0e07" : "Available times"}
              </p>
              {previewSlots.closed ? (
                <div className="text-center py-6">
                  <div className="w-12 h-12 rounded-full bg-muted/50 flex items-center justify-center mx-auto mb-2">
                    <Calendar className="w-5 h-5 text-muted-foreground/50" />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {th ? "\u0e1b\u0e34\u0e14\u0e17\u0e33\u0e01\u0e32\u0e23\u0e27\u0e31\u0e19\u0e19\u0e35\u0e49" : "Closed today"}
                  </p>
                  <p className="text-xs text-muted-foreground/60 mt-1">
                    {th ? "\u0e01\u0e23\u0e38\u0e13\u0e32\u0e40\u0e25\u0e37\u0e2d\u0e01\u0e27\u0e31\u0e19\u0e2d\u0e37\u0e48\u0e19" : "Please select another date"}
                  </p>
                </div>
              ) : previewSlots.slots.length === 0 ? (
                <div className="text-center py-6">
                  <p className="text-sm text-muted-foreground italic">
                    {th ? "\u0e44\u0e21\u0e48\u0e21\u0e35\u0e2a\u0e25\u0e47\u0e2d\u0e15" : "No slots available"}
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-1.5">
                  {previewSlots.slots.map((slot, i) => (
                    <button
                      key={slot}
                      type="button"
                      className={cn(
                        "px-2 py-2 rounded-md text-[12px] font-medium tabular-nums transition-all text-center",
                        i === 0
                          ? "bg-primary text-primary-foreground shadow-sm ring-1 ring-primary/30"
                          : "bg-muted/40 text-foreground hover:bg-muted/70",
                      )}
                    >
                      {formatTime12(slot)}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Mock book button */}
            {!previewSlots.closed && previewSlots.slots.length > 0 && (
              <div className="px-5 pb-5">
                <div className="w-full h-10 rounded-lg bg-primary flex items-center justify-center text-sm font-medium text-primary-foreground">
                  {th ? "\u0e08\u0e2d\u0e07\u0e40\u0e25\u0e22" : "Book Now"} \u2014 {formatTime12(previewSlots.slots[0])}
                </div>
              </div>
            )}

            {/* Footer note */}
            <div className="px-5 py-3 bg-muted/20 border-t border-border/50 text-center">
              <p className="text-[10px] text-muted-foreground">
                {th ? "\u0e15\u0e31\u0e27\u0e2d\u0e22\u0e48\u0e32\u0e07 \u2014 \u0e2d\u0e31\u0e1b\u0e40\u0e14\u0e15\u0e15\u0e32\u0e21\u0e01\u0e32\u0e23\u0e15\u0e31\u0e49\u0e07\u0e04\u0e48\u0e32" : "Live preview \u2014 updates as you configure"}
              </p>
            </div>
          </div>

          {/* Quick stats */}
          <div className={cn("rounded-xl border border-border bg-white dark:bg-card", CARD)}>
            <div className="px-4 py-3 border-b border-border/50">
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                {th ? "\u0e2a\u0e23\u0e38\u0e1b" : "Summary"}
              </span>
            </div>
            <div className="p-0">
              {[
                { label: th ? "\u0e2a\u0e25\u0e47\u0e2d\u0e15" : "Slot", value: formatDuration(slotMins, th), icon: Timer },
                { label: th ? "\u0e1e\u0e23\u0e49\u0e2d\u0e21\u0e01\u0e31\u0e19" : "Concurrent", value: `${maxConcurrent}`, icon: Users },
                { label: th ? "\u0e15\u0e48\u0e2d\u0e01\u0e32\u0e23\u0e08\u0e2d\u0e07" : "Per booking", value: `${minSlots}\u2013${maxSlots} slots`, icon: Layers },
                { label: th ? "\u0e25\u0e48\u0e27\u0e07\u0e2b\u0e19\u0e49\u0e32" : "Advance", value: `${advanceDays} ${th ? "\u0e27\u0e31\u0e19" : "days"}`, icon: CalendarRange },
                { label: th ? "\u0e27\u0e31\u0e19\u0e40\u0e1b\u0e34\u0e14" : "Open days", value: `${openDayCount}/7`, icon: Calendar },
              ].map(({ label, value, icon: Icon }, i) => (
                <div key={label}>
                  {i > 0 && <div className="border-t border-border/50 mx-4" />}
                  <div className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <Icon className="w-3.5 h-3.5 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">{label}</span>
                    </div>
                    <span className="text-sm font-semibold tabular-nums">
                      {value}
                    </span>
                  </div>
                </div>
              ))}
              {!previewSlots.closed && previewSlots.slots.length > 0 && (
                <>
                  <div className="border-t border-border/50 mx-4" />
                  <div className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">
                        {th ? "\u0e01\u0e32\u0e23\u0e08\u0e2d\u0e07\u0e17\u0e31\u0e49\u0e07\u0e2b\u0e21\u0e14/\u0e27\u0e31\u0e19" : "Total/day"}
                      </span>
                    </div>
                    <span className="text-sm font-semibold tabular-nums text-primary">
                      {previewSlots.slots.length * maxConcurrent}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
